import React, { useState } from 'react';
import { H4 } from '@frontend/shadcn/components/typography/h4';
import { LayerGroupIcon } from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { useGetAllSpecificationTemplatesQuery } from '@frontend/app/hooks';
import { useClientFeatureEnabled } from '@frontend/app/hooks/useClientFeatureEnabled';
import { ClientFeature } from '@frontend/app/constants';
import { Button } from '@frontend/shadcn/components/ui/button';
import { CustomProjectTemplateName } from '../../constants';
import { TEMPLATES_FOR_SALES } from '../../TemplatesPage/TemplatesPage';
import { useCampaign } from '../CampaignContext';
import { getCampaignBgColor, getCampaignDescription, getCampaignIconV2 } from '../utils';
import { TEMPLATE_TO_CAMPAIGN_MAP } from '../constants';
import { DuplicateCampaignDrawer } from '../components/DuplicateCampaignDrawer';
import { SelectableCardGroup } from '../../SettingsPage/components/SelectableCardGroup';
import { SectionCardCollapsable } from '../components/SectionCardCollapsable';

export const CampaignTypeSectionV2: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { templateName, setTemplateDetails, setCurrentStep } = useCampaign();
  const isDemoAccount = useClientFeatureEnabled(ClientFeature.DEMO);
  const { data: templatesData } = useGetAllSpecificationTemplatesQuery();

  const campaignOptions = React.useMemo(() => {
    const templates = isDemoAccount
      ? TEMPLATES_FOR_SALES
      : (templatesData?.templates || []);

    return templates
      .concat({
        templateName: CustomProjectTemplateName,
        templateLogoURL: 'https://storage.googleapis.com/aspirex-static-files/new_logo.png',
        workletSpecKeys: [],
      })
      .map((template, index) => {
        const campaignName = TEMPLATE_TO_CAMPAIGN_MAP[template.templateName];
        const iconBgColor = getCampaignBgColor(template.templateName);

        return {
          id: index + 1,
          value: campaignName,
          renderBody: (
            <div className="flex items-center gap-4 p-3 min-h-[88px]">
              <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0', iconBgColor)}>
                <div className="w-6 h-6 text-white flex items-center justify-center">
                  {getCampaignIconV2(template.templateName, 20)}
                </div>
              </div>
              <div className="flex flex-col">
                <H4 className="font-medium mb-1 text-[#162428] text-sm md:text-base">{campaignName}</H4>
                <p className="text-xs md:text-sm text-[#5D6770] mb-0">
                  {getCampaignDescription(template.templateName)}
                </p>
              </div>
            </div>
          ),
        };
      });
  }, [templatesData, isDemoAccount]);

  const handleCampaignTypeSelect = (campaignName: string) => {
    setTemplateDetails(
      campaignName,
      [],
    );
    setCurrentStep(1);
  };

  return (
    <SectionCardCollapsable
      icon={<LayerGroupIcon className="text-[#5D6770]" />}
      title="Campaign Type"
      subtitle="What type of campaign do you want to run?"
      stepNumber={1}
      totalSteps={2}
      isCompleted={!!templateName}
      defaultExpanded
    >
      <SelectableCardGroup
        value={templateName}
        options={campaignOptions}
        onChange={handleCampaignTypeSelect}
        orientation="horizontal"
      />
      <Button
        variant="link"
        className="text-sm font-medium transition-colors w-fit p-0 mt-4 underline"
        onClick={() => setIsDrawerOpen(true)}
      >
        Duplicate Campaign
      </Button>
      <DuplicateCampaignDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
    </SectionCardCollapsable>
  );
};
